import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Fade,
  Slide,
  Zoom,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment
} from '@mui/material';
import {
  ArrowRight,
  Home,
  Car,
  GraduationCap,
  Plane,
  Target,
  Building2,
  BarChart3,
  PiggyBank,
  TrendingUp,
  Wallet,
  DollarSign,
  Sparkles,
  Expand
} from 'lucide-react';

// Importar utilidades y componente
import CardStyleSelector, { getCardStyle } from './CardStyleSelector';
import { 
  formatCurrency, 
  formatNumber,
  getTimeframesByGoal,
  calculateMonthlySavings,
  getTimeRecommendations,
  validateCustomDate,
  getGoalEstimatedCosts,
  getTimeframeDescription,
  timeframeOptions
} from '../utils/financialUtils';

import agtFinService from '../services/agtFinService';

// Datos de configuración - Función que recibe traducción
const getFinancialPurposes = (t) => [
  {
    id: 'expense_control',
    title: t('agtfin.setup.financialProfile.purposes.expense_control.title'),
    description: t('agtfin.setup.financialProfile.purposes.expense_control.description'),
    alternativeTitle: t('agtfin.setup.financialProfile.purposes.expense_control.description'),
    icon: <BarChart3 size={24} />,
    color: '#4CAF50'
  },
  {
    id: 'save_money',
    title: t('agtfin.setup.financialProfile.purposes.save_money.title'),
    description: t('agtfin.setup.financialProfile.purposes.save_money.description'),
    alternativeTitle: t('agtfin.setup.financialProfile.purposes.save_money.description'),
    icon: <PiggyBank size={24} />,
    color: '#2196F3'
  },
  {
    id: 'invest_grow',
    title: t('agtfin.setup.financialProfile.purposes.invest_grow.title'),
    description: t('agtfin.setup.financialProfile.purposes.invest_grow.description'),
    alternativeTitle: t('agtfin.setup.financialProfile.purposes.invest_grow.description'),
    icon: <TrendingUp size={24} />,
    color: '#FF9800'
  },
  {
    id: 'income_distribution',
    title: t('agtfin.setup.financialProfile.purposes.income_distribution.title'),
    description: t('agtfin.setup.financialProfile.purposes.income_distribution.description'),
    alternativeTitle: t('agtfin.setup.financialProfile.purposes.income_distribution.description'),
    icon: <Wallet size={24} />,
    color: '#9C27B0'
  }
];

const getTangibleGoals = (t) => [
  {
    id: 'buy_house',
    title: t('agtfin.setup.financialProfile.tangibleGoals.buy_house'),
    icon: <Home size={18} style={{ marginLeft: '8px', marginRight: '8px' }} />,
    timeframes: [t('agtfin.setup.financialProfile.timeframes.1year'), t('agtfin.setup.financialProfile.timeframes.2years'), t('agtfin.setup.financialProfile.timeframes.3-5years')],
    color: '#FF6B6B'
  },
  {
    id: 'buy_car',
    title: t('agtfin.setup.financialProfile.tangibleGoals.buy_car'),
    icon: <Car size={18} style={{ marginLeft: '8px', marginRight: '8px' }} />,
    timeframes: [t('agtfin.setup.financialProfile.timeframes.6months'), t('agtfin.setup.financialProfile.timeframes.1year'), t('agtfin.setup.financialProfile.timeframes.2years')],
    color: '#4ECDC4'
  },
  {
    id: 'education',
    title: t('agtfin.setup.financialProfile.tangibleGoals.education'),
    icon: <GraduationCap size={18} style={{ marginLeft: '8px', marginRight: '8px' }} />,
    timeframes: [t('agtfin.setup.financialProfile.timeframes.6months'), t('agtfin.setup.financialProfile.timeframes.1year'), t('agtfin.setup.financialProfile.timeframes.2years')],
    color: '#45B7D1'
  },
  {
    id: 'travel',
    title: t('agtfin.setup.financialProfile.tangibleGoals.travel'),
    icon: <Plane size={18} style={{ marginLeft: '8px', marginRight: '8px' }} />,
    timeframes: [t('agtfin.setup.financialProfile.timeframes.6months'), t('agtfin.setup.financialProfile.timeframes.1year'), t('agtfin.setup.financialProfile.timeframes.18months')],
    color: '#96CEB4'
  },
  {
    id: 'emergency_fund',
    title: t('agtfin.setup.financialProfile.tangibleGoals.emergency_fund'),
    icon: <Target size={18} style={{ marginLeft: '8px', marginRight: '8px' }} />,
    timeframes: [t('agtfin.setup.financialProfile.timeframes.6months'), t('agtfin.setup.financialProfile.timeframes.1year')],
    color: '#FECA57'
  },
  {
    id: 'business',
    title: t('agtfin.setup.financialProfile.tangibleGoals.business'),
    icon: <Building2 size={18} style={{ marginLeft: '8px', marginRight: '8px' }} />,
    timeframes: [t('agtfin.setup.financialProfile.timeframes.1year'), t('agtfin.setup.financialProfile.timeframes.2years'), t('agtfin.setup.financialProfile.timeframes.3years')],
    color: '#FF9FF3'
  }
];

const currencies = [
  { value: 'USD', label: '🇺🇸 USD', symbol: '$' },
  { value: 'EUR', label: '🇪🇺 EUR', symbol: '€' },
  { value: 'CLP', label: '🇨🇱 CLP', symbol: '$' },
  { value: 'MXN', label: '🇲🇽 MXN', symbol: '$' },
  { value: 'ARS', label: '🇦🇷 ARS', symbol: '$' },
  { value: 'COP', label: '🇨🇴 COP', symbol: '$' }
];

// Componentes para efectos de tarjeta
const OmpayIcon = ({ size = 60, className = "" }) => (
  <svg 
    width={size} 
    height={size * 0.6} 
    viewBox="0 0 100 60" 
    className={className}
    fill="currentColor"
  >
    <circle cx="20" cy="30" r="15" opacity="0.8"/>
    <circle cx="50" cy="30" r="15" opacity="0.6"/>
    <circle cx="80" cy="30" r="15" opacity="0.4"/>
    <rect x="10" y="45" width="80" height="3" rx="1.5" opacity="0.3"/>
  </svg>
);

const SoundWaveIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 20 20" 
    className={className}
    fill="currentColor"
  >
    <rect x="2" y="8" width="2" height="4" opacity="0.6"/>
    <rect x="6" y="6" width="2" height="8" opacity="0.8"/>
    <rect x="10" y="4" width="2" height="12" opacity="1"/>
    <rect x="14" y="7" width="2" height="6" opacity="0.7"/>
  </svg>
);

// Efecto Liquid Glass
const LiquidGlassEffect = () => {
  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url('https://images.pexels.com/photos/2317711/pexels-photo-2317711.jpeg?auto=compress&cs=tinysrgb&w=800')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
          animation: 'liquidMove 12s ease-in-out infinite',
          '@keyframes liquidMove': {
            '0%, 100%': { 
              transform: 'scale(1.15) translate(0, 0)',
            },
            '25%': { 
              transform: 'scale(1.2) translate(-2%, 1%)',
            },
            '50%': { 
              transform: 'scale(1.25) translate(1%, -2%)',
            },
            '75%': { 
              transform: 'scale(1.2) translate(-1%, -1%)',
            }
          },
          zIndex: 1
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          zIndex: 2,
          backdropFilter: 'blur(4px) saturate(200%)',
          background: 'rgba(255, 255, 255, 0.1)',
          pointerEvents: 'none'
        }}
      />
    </>
  );
};

// Patrón para Apple Cash
const AppleCashPattern = () => {
  const canvasRef = React.useRef(null);
  const allDotsRef = React.useRef([]);
  const animationRef = React.useRef(null);
  const timeRef = React.useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const createPattern = () => {
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      
      canvas.width = rect.width;
      canvas.height = rect.height;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      allDotsRef.current = [];

      const originFocalPoint = { 
        x: canvas.width * 0.5, 
        y: canvas.height * 1.4
      }; 
      
      const rows = 8;
      const baseSizeMin = 1;
      const baseSizeMax = 4;

      for (let i = 0; i < rows; i++) {
        const centerRow = Math.floor(rows / 2);
        const distanceFromCenter = Math.abs(i - centerRow);
        const maxDistance = Math.max(centerRow, rows - centerRow - 1);
        
        const normalizedDistance = distanceFromCenter / maxDistance;
        const sizeMultiplier = 1 - Math.pow(normalizedDistance, 2.5);
        const size = baseSizeMin + (baseSizeMax - baseSizeMin) * sizeMultiplier;
        
        const radius = 30 + (i * 15);
        const startAngle = Math.PI * 0.2;
        const endAngle = Math.PI * 0.8;   
        const totalAngle = endAngle - startAngle;
        
        const circumferenceSegment = radius * totalAngle;
        const idealSpacing = 12;
        const dotsInRow = Math.max(4, Math.floor(circumferenceSegment / idealSpacing));
        const angleStep = totalAngle / Math.max(1, dotsInRow - 1);
        
        for (let j = 0; j < dotsInRow; j++) {
          const angle = startAngle + (j * angleStep);
          
          const x = originFocalPoint.x + radius * Math.cos(angle);
          const y = originFocalPoint.y - radius * Math.sin(angle);
          
          if (x < 15 || x > canvas.width - 15 || y < 8 || y > canvas.height - 8) continue;
          
          const dot = {
            x, y, 
            size, 
            scale: sizeMultiplier,
            rowIndex: i,
            dotIndex: j,
            centerDistance: distanceFromCenter,
            normalizedX: (x - 15) / (canvas.width - 30),
            normalizedY: (y - 8) / (canvas.height - 16)
          };
          
          allDotsRef.current.push(dot);
        }
      }
    };

    const drawPattern = (currentTime) => {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const colorTransitionSpeed = 25;
      const globalHue = (currentTime * colorTransitionSpeed) % 360;
      
      const globalShimmer = Math.sin(currentTime * 0.6) * 0.2 + 0.8;
      
      allDotsRef.current.forEach((dot) => {
        const baseSaturation = 75;
        const baseLightness = 60;
        
        const positionVariation = Math.sin(dot.normalizedX * Math.PI + dot.normalizedY * Math.PI) * 0.08;
        
        const hue = globalHue;
        const saturation = Math.min(90, baseSaturation + (globalShimmer * 12));
        const lightness = Math.max(40, Math.min(75, baseLightness + (globalShimmer * 12) + (positionVariation * 8)));
        
        const gradient = ctx.createRadialGradient(
          dot.x, dot.y, 0,
          dot.x, dot.y, dot.size * 1.3
        );
        
        const centerColor = `hsl(${hue}, ${saturation}%, ${Math.min(80, lightness + 12)}%)`;
        const edgeColor = `hsl(${(hue + 15) % 360}, ${Math.max(45, saturation - 15)}%, ${Math.max(30, lightness - 15)}%)`;
        
        gradient.addColorStop(0, centerColor);
        gradient.addColorStop(1, edgeColor);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.size, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const animate = (currentTime) => {
      const newTime = currentTime * 0.001;
      timeRef.current = newTime;
      drawPattern(newTime);
      animationRef.current = requestAnimationFrame(animate);
    };

    createPattern();
    animationRef.current = requestAnimationFrame(animate);

    const resizeObserver = new ResizeObserver(() => {
      createPattern();
    });
    resizeObserver.observe(canvas);

    return () => {
      resizeObserver.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        opacity: 0.8
      }}
    />
  );
};

// Componente de sección de meta mejorada
const ImprovedGoalSection = ({ formData, onInputChange, getCurrentCurrency }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [monthlySavings, setMonthlySavings] = useState(0);
  const [timeRecommendations, setTimeRecommendations] = useState(null);

  // Obtener opciones de tiempo para la meta seleccionada
  const availableTimeframes = getTimeframesByGoal(formData.primaryGoal);
  
  // Obtener costos estimados para la meta
  const estimatedCosts = getGoalEstimatedCosts(formData.primaryGoal, formData.currency);

  // Calcular ahorro mensual cuando cambien los valores
  useEffect(() => {
    if (formData.goalAmount && formData.goalTimeframe) {
      const targetDate = showCustomDate ? customDate : null;
      const monthly = calculateMonthlySavings(
        parseFloat(formData.goalAmount), 
        formData.goalTimeframe, 
        targetDate
      );
      setMonthlySavings(monthly);
    }
  }, [formData.goalAmount, formData.goalTimeframe, customDate, showCustomDate]);

  // Obtener recomendaciones de tiempo cuando cambie el monto
  useEffect(() => {
    if (formData.goalAmount) {
      const recommendations = getTimeRecommendations(
        parseFloat(formData.goalAmount), 
        formData.currency
      );
      setTimeRecommendations(recommendations);
    }
  }, [formData.goalAmount, formData.currency]);

  const handleTimeframeChange = (value) => {
    if (value === 'custom') {
      setShowCustomDate(true);
      onInputChange('goalTimeframe', value);
    } else {
      setShowCustomDate(false);
      setCustomDate('');
      setDateError('');
      onInputChange('goalTimeframe', value);
    }
  };

  const handleCustomDateChange = (dateString) => {
    setCustomDate(dateString);
    const validation = validateCustomDate(dateString);
    
    if (validation.valid) {
      setDateError('');
      // Actualizar el cálculo de ahorro mensual
      const monthly = calculateMonthlySavings(
        parseFloat(formData.goalAmount || 0), 
        'custom', 
        dateString
      );
      setMonthlySavings(monthly);
    } else {
      setDateError(validation.error);
    }
  };

  const handleAmountChange = (value) => {
    // Limpiar el valor y mantener solo números
    const cleanValue = value.replace(/[^\d]/g, '');
    onInputChange('goalAmount', cleanValue);
  };

  const getDisplayAmount = () => {
    if (!formData.goalAmount) return '';
    return formatNumber(formData.goalAmount, formData.currency);
  };

  return (
    <Box sx={{ 
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
      backdropFilter: 'blur(10px)',
      borderRadius: 3, 
      p: 3,
      mb: 3,
      boxShadow: 'none',
    }}>
      <Typography variant="h6" sx={{ 
        color: theme.palette.mode === 'dark' ? 'white' : 'black',
        mb: 3, 
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1
      }}>
        <TrendingUp size={20} />
        {t('agtfin.setup.financialProfile.goalSection.tellUsMore')}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Campo de monto */}
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ 
            color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
            mb: 1,
            fontWeight: 500
          }}>
            {t('agtfin.setup.financialProfile.prompts.howMuch')}
          </Typography>
          
          <TextField
            fullWidth
            placeholder={t('agtfin.setup.financialProfile.prompts.enterAmount')}
            value={getDisplayAmount()}
            onChange={(e) => handleAmountChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DollarSign size={20} style={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' 
                  }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                height: '50px',
                fontSize: '1.1rem',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input::placeholder': {
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                opacity: 1,
              },
            }}
          />

          {/* Mostrar rango estimado */}
          {estimatedCosts && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 4, 
                height: 4, 
                borderRadius: '50%', 
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' 
              }} />
              <Typography variant="caption" sx={{ 
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              }}>
                {t('agtfin.setup.financialProfile.goalSection.typicalRange')} {estimatedCosts.min} - {estimatedCosts.max}
              </Typography>
            </Box>
          )}
        </Grid>
        
        {/* Campo de tiempo */}
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ 
            color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
            mb: 1,
            fontWeight: 500
          }}>
            {t('agtfin.setup.financialProfile.goalSection.howLong')}
          </Typography>

          <FormControl fullWidth>
            <Select
              value={formData.goalTimeframe || ''}
              onChange={(e) => handleTimeframeChange(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: '20px',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                height: '50px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            >
              <MenuItem value="" disabled>
                {t('agtfin.setup.financialProfile.prompts.selectTimeframe')}
              </MenuItem>
              {availableTimeframes.map((timeframe) => (
                <MenuItem key={timeframe.value} value={timeframe.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'currentColor',
                        opacity: 0.7
                      }} />
                      {timeframe.label}
                    </Box>
                    {timeRecommendations?.recommended.includes(timeframe.value) && (
                      <Box sx={{
                        bgcolor: theme.palette.mode === 'dark' ? 'white' : 'black',
                        color: theme.palette.mode === 'dark' ? 'black' : 'white',
                        px: 1.5,
                        py: 0.3,
                        borderRadius: 2,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}>
                        {t('agtfin.setup.financialProfile.goalSection.recommended')}
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Campo de fecha personalizada */}
          {showCustomDate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ 
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                mb: 1,
                fontWeight: 500
              }}>
                {t('agtfin.setup.financialProfile.goalSection.targetDate')}
              </Typography>

              <TextField
                fullWidth
                type="date"
                value={customDate}
                onChange={(e) => handleCustomDateChange(e.target.value)}
                inputProps={{
                  min: new Date(Date.now() + 86400000).toISOString().split('T')[0] // Mañana
                }}
                error={!!dateError}
                helperText={dateError}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                    height: '50px',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: 'none' },
                  },
                  '& .MuiFormHelperText-root': {
                    color: theme.palette.mode === 'dark' ? 'rgba(255,100,100,0.8)' : 'rgba(200,50,50,0.8)',
                    ml: 0,
                    mt: 1
                  }
                }}
              />
            </Box>
          )}
        </Grid>

        {/* Mostrar cálculo de ahorro mensual */}
        {monthlySavings > 0 && formData.goalAmount && formData.goalTimeframe && (
          <Grid item xs={12}>
            <Box sx={{ 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              borderRadius: 2,
              p: 2,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
            }}>
              <Typography variant="body2" sx={{ 
                fontWeight: 500,
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
              }}>
                {t('agtfin.setup.financialProfile.goalSection.needToSave')}{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {formatCurrency(monthlySavings, formData.currency)}
                </Box>{' '}
                {t('agtfin.setup.financialProfile.goalSection.monthlyToReach')}{' '}
                {showCustomDate && customDate ? (
                  <>{t('agtfin.setup.financialProfile.goalSection.in')} {getTimeframeDescription('custom', customDate)}</>
                ) : (
                  <>{t('agtfin.setup.financialProfile.goalSection.in')} {getTimeframeDescription(formData.goalTimeframe)}</>
                )}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Acordeón con recomendaciones de tiempo */}
        {timeRecommendations && formData.goalAmount && (
          <Grid item xs={12}>
            <Box sx={{ 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              borderRadius: 2,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              overflow: 'hidden'
            }}>
              <Accordion 
                sx={{ 
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 }
                }}
              >
                <AccordionSummary 
                  expandIcon={<Expand style={{ 
                    color: theme.palette.mode === 'dark' ? 'white' : 'black' 
                  }} />}
                  sx={{
                    bgcolor: 'transparent',
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ 
                    fontWeight: 500,
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  }}>
                    {t('agtfin.setup.financialProfile.goalSection.timeRecommendations')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" sx={{ 
                    mb: 2, 
                    opacity: 0.8,
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  }}>
                    {t('agtfin.setup.financialProfile.goalSection.basedOnAmount')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {timeRecommendations.recommended.map((timeframeValue) => {
                      const option = timeframeOptions.find(opt => opt.value === timeframeValue);
                      if (!option) return null;
                      
                      const monthlySavingForTimeframe = calculateMonthlySavings(
                        parseFloat(formData.goalAmount), 
                        timeframeValue
                      );
                      
                      return (
                        <Box
                          key={timeframeValue}
                          onClick={() => handleTimeframeChange(timeframeValue)}
                          sx={{
                            bgcolor: formData.goalTimeframe === timeframeValue 
                              ? (theme.palette.mode === 'dark' ? 'white' : 'black')
                              : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                            color: formData.goalTimeframe === timeframeValue 
                              ? (theme.palette.mode === 'dark' ? 'black' : 'white')
                              : theme.palette.mode === 'dark' ? 'white' : 'black',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                              color: theme.palette.mode === 'dark' ? 'black' : 'white',
                            }
                          }}
                        >
                          {option.label}: {formatCurrency(monthlySavingForTimeframe, formData.currency)}{t('agtfin.setup.financialProfile.goalSection.perMonth')}
                        </Box>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const FinancialProfileSetup = ({ user, onProfileCreated }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const financialPurposes = getFinancialPurposes(t);
  const tangibleGoals = getTangibleGoals(t);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAlternatives, setShowAlternatives] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: user?.displayName || '',
    age: '',
    occupation: '',
    currency: 'USD',
    financialPurpose: '',
    primaryGoal: '',
    goalTimeframe: '',
    goalAmount: '',
    goalDescription: '',
    cardStyle: 'liquid_glass' // Estilo por defecto
  });

  const [currentField, setCurrentField] = useState('fullName');
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStyleSelected = (styleId) => {
    setFormData(prev => ({
      ...prev,
      cardStyle: styleId
    }));
  };

  const handleStyleContinue = () => {
    setStep(1);
  };

  const handlePurposeSelect = (purposeId) => {
    handleInputChange('financialPurpose', purposeId);
    setTimeout(() => {
      setStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 300);
  };

  const handleGoalSelect = (goalId) => {
    handleInputChange('primaryGoal', goalId);
  };

  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const profileData = {
        ...formData,
        profileType: 'goal_oriented',
        createdAt: new Date().toISOString(),
        setupCompleted: true
      };

      console.log('📤 Enviando datos al backend:', JSON.stringify(profileData, null, 2));
      console.log('👤 Usuario ID:', user.uid);

      // ✅ USAR EL SERVICIO REAL
      const response = await agtFinService.saveUserProfile(user.uid, profileData);

      console.log('✅ Perfil creado exitosamente:', response);
      setSuccess(t('agtfin.setup.financialProfile.messages.profileCreatedSuccess'));
      
      setTimeout(() => {
        onProfileCreated && onProfileCreated();
      }, 2500);
      
    } catch (err) {
      console.error('❌ Error completo:', err);
      console.error('❌ Error response:', err.response?.data);

      let errorMessage = t('agtfin.setup.financialProfile.messages.errorCreatingProfile');

      if (err.response?.data?.details) {
        const validationErrors = err.response.data.details.map(detail =>
          `${detail.field}: ${detail.message}`
        ).join(', ');
        errorMessage = `${t('agtfin.setup.financialProfile.messages.validationErrors')} ${validationErrors}`;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentCurrency = () => {
    return currencies.find(c => c.value === formData.currency);
  };

  const toggleAlternatives = () => {
    setShowAlternatives(!showAlternatives);
  };

  const isStep1Complete = formData.fullName && formData.age && formData.occupation && formData.currency;
  const isStep3Complete = formData.primaryGoal && formData.goalTimeframe;

  // Función para renderizar el contenido de la tarjeta según el estilo
  const renderCardContent = () => {
    if (formData.cardStyle === 'apple_cash') {
      return (
        <Box sx={{ position: 'relative', zIndex: 5, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0 }}>
            <Box>
              <Typography variant="h6" sx={{ 
                fontFamily: 'monospace',
                letterSpacing: 1,
                fontSize: '1rem',
                mb: 0.3,
                lineHeight: 1,
                textShadow: '0 0 10px rgba(0,0,0,0.8)'
              }}>
                {formData.fullName || t('agtfin.setup.financialProfile.placeholders.fullName')}
              </Typography>
              <Typography variant="caption" sx={{ 
                opacity: 0.8,
                fontSize: '0.75rem',
                lineHeight: 1,
                textShadow: '0 0 8px rgba(0,0,0,0.6)'
              }}>
                {t('agtfin.setup.financialProfile.brandNames.agtFinance')}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ 
              opacity: 0.8,
              fontSize: '0.875rem',
              textShadow: '0 0 8px rgba(0,0,0,0.6)'
            }}>
              {getCurrentCurrency()?.label}
            </Typography>
          </Box>
          
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7, textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
                  {t('agtfin.setup.financialProfile.cardLabels.age')}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
                  {formData.age || '--'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7, textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
                  {t('agtfin.setup.financialProfile.cardLabels.occupation')}
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  textShadow: '0 0 8px rgba(0,0,0,0.6)'
                }}>
                  {formData.occupation || '----------'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={{ position: 'relative', zIndex: 5, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <OmpayIcon size={60} className="text-gray-300" />
            <Typography variant="body2" sx={{ 
              opacity: 0.8,
              textShadow: formData.cardStyle === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
            }}>
              {getCurrentCurrency()?.label}
            </Typography>
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ mb: 1 }}>
            <Typography variant="h5" sx={{ 
              fontFamily: 'monospace',
              letterSpacing: 2,
              mb: 1,
              minHeight: 32,
              textShadow: formData.cardStyle === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
            }}>
              {formData.fullName || t('agtfin.setup.financialProfile.placeholders.fullName')}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
              <Box>
                <Typography variant="caption" sx={{
                  opacity: 0.7,
                  textShadow: formData.cardStyle === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
                }}>
                  {t('agtfin.setup.financialProfile.cardLabels.age')}
                </Typography>
                <Typography variant="body1" sx={{
                  fontFamily: 'monospace',
                  textShadow: formData.cardStyle === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
                }}>
                  {formData.age || '--'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{
                  opacity: 0.7,
                  textShadow: formData.cardStyle === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
                }}>
                  {t('agtfin.setup.financialProfile.cardLabels.occupation')}
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  textShadow: formData.cardStyle === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
                }}>
                  {formData.occupation || '----------'}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ 
            position: 'absolute',
            bottom: 16,
            right: 16,
            display: 'flex', 
            gap: 0.2
          }}>
            <SoundWaveIcon size={16} className="text-white" />
            <SoundWaveIcon size={16} className="text-white" />
          </Box>
        </Box>
      );
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      height: '100%',
      position: 'relative',
      overflow: 'auto',
      backgroundColor: 'transparent',
    }}>
      <Box sx={{
        width: '100%',
        pb: { xs: '80px', sm: '100px', md: '120px' },
        pt: { xs: '20px', sm: '40px', md: '60px' },
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: { xs: 2, sm: 3, md: 4 },
      }}>
        <Box sx={{ maxWidth: 500, width: '100%' }}>
          {/* Título */}
          <Fade in timeout={1000}>
            <Typography 
              variant="h3" 
              align="center" 
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                mb: 4,
                fontWeight: 700,
                textShadow: theme.palette.mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {t('agtfin.setup.financialProfile.brandNames.agtFin')}
            </Typography>
          </Fade>

          {/* Paso 0: Selector de estilo de tarjeta */}
          {step === 0 && (
            <CardStyleSelector 
              onStyleSelected={handleStyleSelected}
              selectedStyle={formData.cardStyle}
              onContinue={handleStyleContinue}
            />
          )}

          {/* Tarjeta de Crédito - Solo mostrar después del paso 0 y antes del paso 2 */}
          {(step === 1) && (
            <Box sx={{ 
              mb: 4,
              height: 240,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <Card
                sx={{
                  width: '100%',
                  height: 240,
                  borderRadius: 4,
                  p: 3,
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.1)',
                  ...getCardStyle(formData.cardStyle)
                }}
              >
                {/* Efectos según el estilo */}
                {formData.cardStyle === 'omverse' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `
                        radial-gradient(circle at 20% 20%, rgba(255,255,255,0.3) 1px, transparent 1px),
                        radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 1px, transparent 1px),
                        radial-gradient(circle at 40% 80%, rgba(255,255,255,0.3) 1px, transparent 1px),
                        radial-gradient(circle at 60% 60%, rgba(255,255,255,0.1) 1px, transparent 1px)
                      `,
                      backgroundSize: '50px 50px, 75px 75px, 60px 60px, 40px 40px',
                      backgroundPosition: '0 0, 25px 25px, 50px 10px, 75px 35px',
                      opacity: 0.4,
                      pointerEvents: 'none'
                    }}
                  />
                )}
                {formData.cardStyle === 'liquid_glass' && <LiquidGlassEffect />}
                {formData.cardStyle === 'apple_cash' && <AppleCashPattern />}

                {renderCardContent()}
              </Card>
            </Box>
          )}

          {/* Contenido dinámico según el paso */}
          {step === 1 && (
            <Fade in timeout={800}>
              <Box>
                <Typography 
                  variant="h5" 
                  align="center" 
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                    mb: 3, 
                    fontWeight: 600 
                  }}
                >
                  {currentField === 'fullName' && t('agtfin.setup.financialProfile.fields.fullName')}
                  {currentField === 'age' && t('agtfin.setup.financialProfile.fields.age')}
                  {currentField === 'occupation' && t('agtfin.setup.financialProfile.fields.occupation')}
                  {currentField === 'currency' && t('agtfin.setup.financialProfile.fields.currency')}
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  {currentField !== 'currency' ? (
                    <TextField
                      fullWidth
                      value={formData[currentField]}
                      onChange={(e) => handleInputChange(currentField, e.target.value)}
                      placeholder={
                        currentField === 'fullName' ? t('agtfin.setup.financialProfile.placeholders.fullName') :
                        currentField === 'age' ? t('agtfin.setup.financialProfile.placeholders.age') :
                        t('agtfin.setup.financialProfile.placeholders.occupation')
                      }
                      type={currentField === 'age' ? 'number' : 'text'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '25px',
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                          backdropFilter: 'blur(10px)',
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontSize: '1.2rem',
                          height: '60px',
                          '& fieldset': {
                            border: 'none',
                          },
                          '&:hover fieldset': {
                            border: 'none',
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                          },
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          opacity: 1,
                        },
                      }}
                    />
                  ) : (
                    <FormControl fullWidth>
                      <Select
                        value={formData.currency}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                        sx={{
                          borderRadius: '25px',
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                          backdropFilter: 'blur(10px)',
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          height: '60px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {/* Navegación entre campos */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Button
                    onClick={() => {
                      const fields = ['fullName', 'age', 'occupation', 'currency'];
                      const currentIndex = fields.indexOf(currentField);
                      if (currentIndex > 0) {
                        setCurrentField(fields[currentIndex - 1]);
                      }
                    }}
                    disabled={currentField === 'fullName'}
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'black' : 'white',
                      bgcolor: theme.palette.mode === 'dark' ? 'white' : 'black',
                      borderRadius: '25px',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                      },
                      '&:disabled': {
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                        color: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
                      }
                    }}
                    variant="contained"
                  >
                    {t('agtfin.setup.financialProfile.buttons.previous')}
                  </Button>

                  <Button
                    onClick={() => {
                      const fields = ['fullName', 'age', 'occupation', 'currency'];
                      const currentIndex = fields.indexOf(currentField);
                      if (currentIndex < fields.length - 1) {
                        setCurrentField(fields[currentIndex + 1]);
                      }
                    }}
                    disabled={currentField === 'currency'}
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'black' : 'white',
                      bgcolor: theme.palette.mode === 'dark' ? 'white' : 'black',
                      borderRadius: '25px',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                      },
                      '&:disabled': {
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                        color: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
                      }
                    }}
                    variant="contained"
                  >
                    {t('agtfin.setup.financialProfile.buttons.continue')}
                  </Button>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleNext}
                  disabled={!isStep1Complete}
                  endIcon={<ArrowRight size={20} />}
                  sx={{
                    borderRadius: '25px',
                    height: '50px',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    bgcolor: theme.palette.mode === 'dark' ? 'white' : 'black',
                    color: theme.palette.mode === 'dark' ? 'black' : 'white',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                      boxShadow: 'none',
                      transform: 'scale(1.02)'
                    },
                    '&:disabled': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                      color: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
                      transform: 'none'
                    }
                  }}
                >
                  {t('agtfin.setup.financialProfile.buttons.continue')}
                </Button>
              </Box>
            </Fade>
          )}

          {/* Paso 2: Selección de propósito financiero - Sin tarjeta */}
          {step === 2 && (
            <Fade in timeout={800}>
              <Box>
                <Typography 
                  variant="h5" 
                  align="center" 
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                    mb: 4, 
                    fontWeight: 600 
                  }}
                >
                  {t('agtfin.setup.financialProfile.purposeSection.whatDoYouWant')}
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {financialPurposes.map((purpose, index) => (
                    <Grid item xs={6} key={purpose.id}>
                      <Zoom in timeout={400 + index * 100}>
                        <Card 
                          sx={{ 
                            cursor: 'pointer',
                            background: theme.palette.mode === 'dark' 
                              ? 'rgba(255,255,255,0.15)' 
                              : 'rgba(0,0,0,0.05)',
                            backdropFilter: 'blur(10px)',
                            height: '120px',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.3s ease',
                            boxShadow: 'none',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              background: theme.palette.mode === 'dark' 
                                ? 'rgba(255,255,255,0.25)' 
                                : 'rgba(0,0,0,0.1)',
                              boxShadow: 'none',
                            }
                          }}
                          onClick={() => handlePurposeSelect(purpose.id)}
                        >
                          <CardContent sx={{ 
                            textAlign: 'center', 
                            py: 2, 
                            px: 2, 
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                          }}>
                            <Box sx={{ 
                              mb: 1,
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {purpose.icon}
                            </Box>
                            <Typography variant="caption" sx={{ 
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                              fontWeight: 600, 
                              fontSize: '0.85rem',
                              lineHeight: 1.2,
                              display: 'block'
                            }}>
                              {showAlternatives ? purpose.alternativeTitle : purpose.title}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    onClick={toggleAlternatives}
                    sx={{
                      background: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.15)' 
                        : 'rgba(0,0,0,0.05)',
                      backdropFilter: 'blur(10px)',
                      color: theme.palette.mode === 'dark' ? 'white' : 'black',
                      borderRadius: '20px',
                      px: 3,
                      py: 1,
                      fontSize: '0.85rem',
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        background: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.25)' 
                          : 'rgba(0,0,0,0.1)',
                        boxShadow: 'none',
                      }
                    }}
                  >
                    {t('agtfin.setup.financialProfile.purposeSection.seeMore')}
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}

          {step === 3 && (
            <Fade in timeout={800}>
              <Box>
                <Typography
                  variant="h5"
                  align="center"
                  sx={{
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                    mb: 4,
                    fontWeight: 600
                  }}
                >
                  {t('agtfin.setup.financialProfile.prompts.mainGoal')}
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mb: 4 }}>
                  {tangibleGoals.map((goal, index) => (
                    <Zoom in timeout={400 + index * 100} key={goal.id}>
                      <Chip
                        icon={goal.icon}
                        label={goal.title}
                        onClick={() => handleGoalSelect(goal.id)}
                        sx={{
                          bgcolor: formData.primaryGoal === goal.id 
                            ? (theme.palette.mode === 'dark' ? 'white' : 'black')
                            : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                          color: formData.primaryGoal === goal.id 
                            ? (theme.palette.mode === 'dark' ? 'black' : 'white')
                            : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                          backdropFilter: 'blur(10px)',
                          fontSize: '0.9rem',
                          height: '40px',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? 'white' : 'black',
                            color: theme.palette.mode === 'dark' ? 'black' : 'white',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s ease',
                            boxShadow: 'none',
                          },
                          '& .MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    </Zoom>
                  ))}
                </Box>

                {formData.primaryGoal && (
                  <Slide direction="up" in timeout={600}>
                    <Box>
                      <ImprovedGoalSection 
                        formData={formData}
                        onInputChange={handleInputChange}
                        getCurrentCurrency={getCurrentCurrency}
                      />
                    </Box>
                  </Slide>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={loading || !isStep3Complete}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Sparkles size={20} />}
                  sx={{
                    borderRadius: '25px',
                    height: '50px',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    bgcolor: theme.palette.mode === 'dark' ? 'white' : 'black',
                    color: theme.palette.mode === 'dark' ? 'black' : 'white',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                      boxShadow: 'none',
                      transform: 'scale(1.02)'
                    },
                    '&:disabled': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                      color: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
                      boxShadow: 'none',
                      transform: 'none'
                    }
                  }}
                >
                  {loading ? t('agtfin.setup.financialProfile.messages.creatingProfile') : t('agtfin.setup.financialProfile.messages.start')}
                </Button>
              </Box>
            </Fade>
          )}

          {/* Alertas */}
          {error && (
            <Fade in>
              <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>
                {error}
              </Alert>
            </Fade>
          )}

          {success && (
            <Fade in>
              <Alert severity="success" sx={{ mt: 2, borderRadius: 3 }}>
                {success}
              </Alert>
            </Fade>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FinancialProfileSetup;